import { useContext, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ToastContainer } from "react-toastify";
import { appPages, appRoutes } from "./router";
import TopicPage from "./ui/pages/TopicPage";
import TopicPartPage from "./ui/pages/TopicPartPage";
import { useTranslation } from "react-i18next";
import { useLanguageStore } from "./core/store/languageStore";
import "react-toastify/dist/ReactToastify.css";
import "./locales/i18n";
import {
  ThemeContext,
  ThemeProvider,
} from "./ui/components/utils/ThemeContext";
import { useAuth } from "./core/hooks/useAuth";
import { imageCacheService } from "./providers/service/imageCacheService";
import { EditModeProvider } from "./ui/components/utils/EditModeContext";
import { UnsavedChangesProvider } from "./ui/components/utils/UnsavedChangesContext";

const queryClient = new QueryClient();

imageCacheService.clean();

export default function App() {
  const { i18n } = useTranslation();
  const { language } = useLanguageStore();

  useEffect(() => {
    if (language) {
      i18n.changeLanguage(language);
    }
  }, [i18n, language]);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <EditModeProvider>
          <UnsavedChangesProvider>
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <AuthWrapper>
              <Routes>
                {Object.values(appPages)
                  .filter((route) => route.path !== appRoutes.topic)
                  .map((route) => (
                    <Route
                      key={route.path}
                      path={route.path}
                      element={route.element}
                    />
                  ))}
                <Route path={appRoutes.topic} element={<TopicPage />}>
                  <Route path=":topicPartId" element={<TopicPartPage />} />
                </Route>

                {/* Route 404 */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <Toast />
            </AuthWrapper>
          </BrowserRouter>
          </UnsavedChangesProvider>
        </EditModeProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

function AuthWrapper({ children }: { children: React.ReactNode }) {
  useAuth({});

  return children;
}

const Toast = () => {
  const { darkMode } = useContext(ThemeContext);

  return (
    <ToastContainer
      position="top-right"
      autoClose={1000}
      theme={darkMode ? "dark" : "light"}
    />
  );
};
